// Пользователи и права: то, чем администратор настраивает систему под заказчика.
//
// Фраза для входа уходит на сервер и обратно НЕ возвращается никогда: в ответе есть только
// признак «задана или нет». По нему администратор видит, кому ещё нельзя войти.

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { AuthResponse } from '@/shared/api/endpoints/auth';
import type { ListResponse } from '@/shared/api/types';

export interface UserOut {
  id: string;
  login: string;
  display_name: string;
  is_active: boolean;
  employee_id?: string | null;
  employee_name?: string | null;
  roles: string[];
  site_ids: string[];
  site_names: string[];
  last_login_at?: string | null;
  created_at: string;
  has_password: boolean;
  /** Пускают ли человека в программу на компьютере. Роль этого не даёт — выдают поштучно. */
  cabinet_access: boolean;
  /**
   * Какие служебные переписки включены: `assistant`, `avito`, `site_leads`.
   *
   * Право их больше не открывает: право про данные, а переписка про то, куда они приходят.
   * По умолчанию не включена ни одна — решение владельца 11.09.2026.
   */
  bot_chats: string[];
  /** Есть живой код приглашения: выдан, не использован, не отозван, не истёк. */
  invite_live: boolean;
  /** Последние знаки живого кода. Сам код не хранится и восстановлению не подлежит. */
  invite_hint?: string | null;
  invite_expires_at?: string | null;
  /** Когда запись убрали из системы. Пусто — живая. Убранные в список не попадают. */
  archived_at?: string | null;
}

export interface UserIn {
  login: string;
  display_name: string;
  employee_id?: string | null;
  roles: string[];
  site_ids?: string[];
  password?: string;
  /** Пускать ли в программу на компьютере. По умолчанию нет. */
  cabinet_access?: boolean;
}

export interface UserPatch {
  display_name?: string;
  employee_id?: string | null;
  is_active?: boolean;
  password?: string;
  cabinet_access?: boolean;
}

export function listUsers(
  params: { q?: string; active_only?: boolean; include_archived?: boolean } = {},
  signal?: AbortSignal,
): Promise<ListResponse<UserOut>> {
  return request<ListResponse<UserOut>>(`/api/v1/users${queryString(params)}`, {
    signal,
  });
}

export interface RoleOut {
  code: string;
  /** Название по-русски приходит с сервера: словарь ролей живёт в контракте. */
  title: string;
  short: string;
  scope: string;
  /**
   * Может ли спрашивающий выдать эту роль. Считает сервер по своей лестнице старшинства.
   *
   * Роль выше своей не выдаётся никому: иначе тот, кому доверили раздачу доступов, выпишет
   * себе руководство вместе со списанием долгов и сторно уже выданных денег. Решать это на
   * экране нельзя — вторая копия лестницы разъехалась бы с сервером молча.
   */
  assignable: boolean;
}

export function listRoles(signal?: AbortSignal): Promise<RoleOut[]> {
  return request<RoleOut[]>('/api/v1/users/roles', { signal });
}

export function createUser(body: UserIn): Promise<UserOut> {
  return request<UserOut>('/api/v1/users', { method: 'POST', body });
}

export function patchUser(userId: string, body: UserPatch): Promise<UserOut> {
  return request<UserOut>(`/api/v1/users/${userId}`, { method: 'PATCH', body });
}

/**
 * Убрать учётную запись из системы: строка уходит из списка, имя входа освобождается,
 * код на руках гаснет. Сама строка в базе остаётся — на неё смотрят журнал и деньги.
 */
export function deleteUser(userId: string): Promise<void> {
  return request<void>(`/api/v1/users/${userId}`, { method: 'DELETE' });
}

export function setUserRoles(userId: string, roles: string[]): Promise<UserOut> {
  return request<UserOut>(`/api/v1/users/${userId}/roles`, {
    method: 'PUT',
    body: { roles },
  });
}

/** Служебные переписки заменяются целиком — как роли: снятая обязана исчезнуть сразу. */
export function setUserBotChats(userId: string, botChats: string[]): Promise<UserOut> {
  return request<UserOut>(`/api/v1/users/${userId}/bot-chats`, {
    method: 'PUT',
    body: { bot_chats: botChats },
  });
}

export function setUserSites(userId: string, siteIds: string[]): Promise<UserOut> {
  return request<UserOut>(`/api/v1/users/${userId}/sites`, {
    method: 'PUT',
    body: { site_ids: siteIds },
  });
}

/**
 * Отозвать живые коды приглашения человека.
 *
 * Отдельно от выдачи, хотя выдача тоже гасит прежние: это разные действия. Выдача говорит «вот
 * новый код», отзыв — «прежний больше не работает, нового не будет». Второе нужно, когда
 * телефон потерян или человек уволился до первого входа.
 *
 * Возвращает, сколько кодов отозвано. Ноль — отзывать было нечего, и сказать об этом человеку
 * надо словами: молчаливое «готово» он прочитает как «код погашен».
 */
export interface BulkInviteRow {
  user_id: string;
  display_name: string;
  login: string;
  /** Полный код. Пусто у пропущенных: показать его второй раз нельзя. */
  code: string | null;
  expires_at: string | null;
  skipped_reason: string | null;
}

export interface BulkInviteOut {
  issued: number;
  skipped: number;
  rows: BulkInviteRow[];
}

/**
 * Коды приглашения пачкой.
 *
 * У заказчика 281 охранник, и каждому нужен свой код, чтобы войти и увидеть свой расчётный
 * лист. По одному это 281 открытая карточка за трое суток, пока код живёт. Полный код каждого
 * возвращается ровно один раз — в этом ответе.
 */
export function issueInvitesBulk(userIds: string[]): Promise<BulkInviteOut> {
  return request<BulkInviteOut>('/api/v1/users/invites', {
    method: 'POST',
    body: { user_ids: userIds },
  });
}

export function revokeInvite(userId: string): Promise<{ revoked: number; user_id: string }> {
  return request(`/api/v1/users/${userId}/invite/revoke`, { method: 'POST' });
}

/**
 * Код приглашения для входа в ПРИЛОЖЕНИЕ НА ТЕЛЕФОНЕ. Полный код показывается ровно один раз.
 *
 * Ссылку собирает сервер, и до 13.09.2026 веб её терял: тип объявлял три поля из шести, то есть
 * готовую ссылку приходилось бы собирать заново руками на каждом экране. Диктовать восемь знаков
 * человеку в перчатке дороже, чем прислать ссылку, — ровно поэтому в приложении ссылка стоит
 * первым действием (`ios/Bark/Features/Access/AccessScreen.swift`).
 */
export interface InviteOut {
  /** Полный код. Второй раз его показать нельзя: в базе лежит отпечаток. */
  code: string;
  /** Две последние буквы: по ним человек сверяет свой код со строкой списка. */
  code_hint: string;
  expires_at: string;
  issued_at: string;
  user_id: string;
  /** Тот же код ссылкой: человек нажимает её и входит, не набирая знаки. */
  link: string;
}

export function issueInvite(userId: string): Promise<InviteOut> {
  return request<InviteOut>(`/api/v1/users/${userId}/invite`, { method: 'POST' });
}

/**
 * Сменить СЕБЕ имя входа и фразу.
 *
 * Нынешняя фраза спрашивается всегда, даже когда меняют только имя: без неё оставленный на
 * минуту без присмотра компьютер означает угон учётной записи.
 *
 * В ответ приезжает НОВЫЙ вход. Так и задумано: смена гасит все входы человека на всех
 * устройствах — в этом её смысл, — а свой возвращается тут же, чтобы не выкидывать на экран
 * входа того, кто просто поменял фразу.
 */
export interface CredentialsIn {
  current_password: string;
  login?: string;
  password?: string;
  device_kind?: 'web' | 'desktop';
}

export function changeMyCredentials(body: CredentialsIn): Promise<AuthResponse> {
  return request<AuthResponse>('/api/v1/users/me/credentials', { method: 'PATCH', body });
}

/**
 * Закрыть СВОЮ учётную запись. Права не требует: человек распоряжается своим доступом.
 *
 * Требование магазина Apple (правило 5.1.1(v)): раз учётная запись есть, из приложения
 * должно быть видно, как её закрыть. Возвращается пустота: после ответа сервера ключи
 * мертвы, и читать из этого ответа нечего — следующий шаг всегда один, экран входа.
 */
export function deleteMyself(): Promise<void> {
  return request<void>('/api/v1/users/me', { method: 'DELETE' });
}
